import React, { useState , useRef} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { serverHost } from '../../../redux/reducers/reducer';

export default function RtkStationConfigDialog({ open, onClose, station, onRefresh }) {
  const [operation, setOperation] = useState('');
  const [restartMode, setRestartMode] = useState('soft'); // 'soft' or 'hard'
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [alt, setAlt] = useState('');
  const [surveyInMode, setSurveyInMode] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [newSiteName, setNewSiteName] = useState(''); // For updating registered stations
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Refs to store current coordinate values for synchronous reads
  const latRef = useRef('');
  const lonRef = useRef('');
  const altRef = useRef('');

  // Helper functions to update both state and refs simultaneously
  const updateLat = (value) => {
    latRef.current = value;
    setLat(value);
  };

  const updateLon = (value) => {
    lonRef.current = value;
    setLon(value);
  };

  const updateAlt = (value) => {
    altRef.current = value;
    setAlt(value);
  };

  // Check if station is registered (has is_registered flag or status is not unregistered)
  const isRegistered = station?.is_registered === true || 
    (station?.status !== 'unregistered' && station?.status !== 'unregistered_inactive');

  // Reset form when dialog opens/closes or station changes
  React.useEffect(() => {
    if (open && station) {
      setOperation('');
      setRestartMode('soft');
      const latVal = station.coordinates?.lat?.toString() || '';
      const lonVal = station.coordinates?.lon?.toString() || '';
      const altVal = station.coordinates?.alt?.toString() || '';
      updateLat(latVal);
      updateLon(lonVal);
      updateAlt(altVal);
      setSurveyInMode(false);
      setSiteName('');
      setNewSiteName(station?.station_name || '');
      setError(null);
      setSuccess(null);
      setShowConfirmation(false);
    }
  }, [open, station]);

  // Reset operation if register_new_site is selected but station is not unregistered
  React.useEffect(() => {
    if (operation === 'register_new_site' && isRegistered) {
      setOperation('');
      setError('Register New Site is only available for unregistered stations');
    }
    if (operation === 'update_site' && !isRegistered) {
      setOperation('');
      setError('Update Site is only available for registered stations');
    }
  }, [operation, station, isRegistered]);

  const handleOperationChange = (event) => {
    setOperation(event.target.value);
    setError(null);
    setSuccess(null);
  };

  const handleSend = async () => {
    if (!operation) {
      setError('Please select an operation');
      return;
    }

    if (!station || !station.port) {
      setError('Station port is missing');
      return;
    }

    // For register_new_site (unregistered stations), show confirmation dialog first
    if (operation === 'register_new_site') {
      // Validate that station is unregistered
      if (isRegistered) {
        setError('Register New Site is only available for unregistered stations');
        return;
      }

      // Validate site_name before showing confirmation
      if (!siteName || siteName.trim() === '') {
        setError('Site Name is required');
        return;
      }

      // Show confirmation dialog
      setShowConfirmation(true);
      setError(null);
      setSuccess(null);
      return;
    }

    // For update_site (registered stations)
    if (operation === 'update_site') {
      if (!isRegistered) {
        setError('Update Site is only available for registered stations');
        return;
      }

      if (!newSiteName || newSiteName.trim() === '') {
        setError('New Site Name is required');
        return;
      }
    }

    // For configure operation - validate coordinates first
    if (operation === 'configure') {
      // Validate coordinates are provided
      if (!lat || !lon || !alt) {
        setError('Latitude, Longitude, and Altitude are required');
        return;
      }

      const latNum = parseFloat(lat);
      const lonNum = parseFloat(lon);
      const altNum = parseFloat(alt);

      if (isNaN(latNum) || isNaN(lonNum) || isNaN(altNum)) {
        setError('Coordinates must be valid numbers');
        return;
      }

      // For configure operation with survey_in_mode enabled
      if (surveyInMode) {
        // Check if coordinates are NOT 0.0
        const coordsAreZero = latNum === 0 && lonNum === 0 && altNum === 0;
        
        if (!coordsAreZero) {
          // Show confirmation dialog asking to set coordinates to 0
          Swal.fire({
            title: 'Survey-In Mode Requires Zero Coordinates',
            html: `Survey-in mode cannot work with non-zero coordinates.<br/>
                   Current coordinates: Lat=${latNum}, Lon=${lonNum}, Alt=${altNum}<br/><br/>
                   Do you want to set coordinates to 0.0 and start survey-in?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, set to 0.0 and continue',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
          }).then((result) => {
            if (result.isConfirmed) {
              // Set coordinates to 0.0 in both state and refs (synchronous update)
              updateLat('0');
              updateLon('0');
              updateAlt('0');
              // Proceed with execution (reset warning will show after completion)
              executeSend();
            }
            // If cancelled, do nothing (user can modify coordinates or uncheck survey-in)
          });
          return; // Don't proceed immediately, wait for user confirmation
        }
      }

      // For all configure operations, proceed directly (reset warning will show after completion)
      await executeSend();
      return;
    }

    // For other operations, proceed directly
    await executeSend();
  };

  const executeSend = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setShowConfirmation(false);

    const serverhost = serverHost();

    try {
      let response;

      // Handle register_new_site - POST to /rtk/addNewRTKStation
      if (operation === 'register_new_site') {
        if (!siteName || siteName.trim() === '') {
          setError('Site Name is required');
          setLoading(false);
          return;
        }

        response = await axios.post(
          `${serverhost}/rtk/addNewRTKStation`,
          {
            station_name: siteName.trim(),
            socket_address: station.port
          },
          { withCredentials: true }
        );

        if (response.status === 200) {
          // Close the dialog immediately with success status and station name
          // The parent will show success dialog and refresh the list
          onClose(true, siteName.trim());
        } else {
          setError('Failed to register new site');
        }
        return;
      }

      // Handle update_site - PUT to /rtk/updateRTKStation
      if (operation === 'update_site') {
        if (!newSiteName || newSiteName.trim() === '') {
          setError('New Site Name is required');
          setLoading(false);
          return;
        }

        response = await axios.put(
          `${serverhost}/rtk/updateRTKStation`,
          {
            station_name: station.station_name,
            new_name: newSiteName.trim()
          },
          { withCredentials: true }
        );

        if (response.status === 200) {
          const successMessage = 'Site updated successfully! The list will refresh automatically.';
          setSuccess(successMessage);
          // Refresh the stations list without closing the dialog
          if (onRefresh) {
            onRefresh();
          }
        } else {
          setError('Failed to update site');
        }
        return;
      }

      // Handle restart - POST to /rtk/restartRTKUblox
      if (operation === 'restart') {
        response = await axios.post(
          `${serverhost}/rtk/restartRTKUblox`,
          {
            port: station.port,
            params: {
              mode: restartMode
            }
          },
          { withCredentials: true }
        );
      } 
      // Handle configure - POST to /rtk/configureRTKUblox
      else if (operation === 'configure') {
        // Read from refs to get current values (synchronous, always up-to-date)
        const latValue = latRef.current;
        const lonValue = lonRef.current;
        const altValue = altRef.current;
        
        // Validate coordinates
        if (!latValue || !lonValue || !altValue) {
          setError('Latitude, Longitude, and Altitude are required');
          setLoading(false);
          return;
        }

        const latNum = parseFloat(latValue);
        const lonNum = parseFloat(lonValue);
        const altNum = parseFloat(altValue);

        if (isNaN(latNum) || isNaN(lonNum) || isNaN(altNum)) {
          setError('Coordinates must be valid numbers');
          setLoading(false);
          return;
        }

        response = await axios.post(
          `${serverhost}/rtk/configureRTKUblox`,
          {
            port: station.port,
            params: {
              lat: latNum,
              lon: lonNum,
              alt: altNum,
              survey_in_mode: surveyInMode
            }
          },
          { withCredentials: true }
        );
      }

      if (response.status === 200) {
        const successMessage = response.data?.message || response.data?.data?.message || 'Operation completed successfully';
        setSuccess(successMessage);
        
        // For configure operations, show reset warning dialog after successful execution
        if (operation === 'configure') {
          Swal.fire({
            title: 'Configuration Update Warning',
            html: `The configuration only updates the station parameters.<br/><br/>
                   <strong>You MUST do a soft or hard reset to see the changes on the u-blox device.</strong>`,
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3085d6'
          });
        }
      } else {
        setError('Failed to execute operation');
      }
    } catch (err) {
      console.error('Error executing RTK operation:', err);
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setShowConfirmation(false);
      onClose(false);
    }
  };

  const handleConfirm = () => {
    setShowConfirmation(false);
    executeSend();
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1000 }}
    >
      <DialogTitle className="display_center">
        Configure RTK Station: {station?.station_name || 'Unknown'}
      </DialogTitle>
      
      <DialogContent sx={{ minWidth: 400, paddingTop: 2 }}>
        {/* Operation Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="operation-select-label">Operation</InputLabel>
          <Select
            labelId="operation-select-label"
            id="operation-select"
            value={operation}
            label="Operation"
            onChange={handleOperationChange}
            disabled={loading}
          >
            <MenuItem value="restart">Restart</MenuItem>
            <MenuItem value="configure">Configure RTK</MenuItem>
            {/* Only show Register New Site for unregistered stations */}
            {!isRegistered && (
              <MenuItem value="register_new_site">Register New Site</MenuItem>
            )}
            {/* Only show Update Site for registered stations */}
            {isRegistered && (
              <MenuItem value="update_site">Update Site Name</MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Restart Operation Fields */}
        {operation === 'restart' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Restart Mode:
            </Typography>
            <FormControl fullWidth>
              <Select
                value={restartMode}
                onChange={(e) => setRestartMode(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="soft">Soft Reset</MenuItem>
                <MenuItem value="hard">Hard Reset</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Soft Reset: Restarts the RTK server script
              <br />
              Hard Reset: Reboots the entire system
            </Typography>
          </Box>
        )}

        {/* Configure RTK Operation Fields */}
        {operation === 'configure' && (
          <Box>
            <TextField
              fullWidth
              label="Latitude"
              type="number"
              value={lat}
              onChange={(e) => updateLat(e.target.value)}
              margin="normal"
              disabled={loading}
              inputProps={{ step: "any" }}
              required
            />
            <TextField
              fullWidth
              label="Longitude"
              type="number"
              value={lon}
              onChange={(e) => updateLon(e.target.value)}
              margin="normal"
              disabled={loading}
              inputProps={{ step: "any" }}
              required
            />
            <TextField
              fullWidth
              label="Altitude (meters)"
              type="number"
              value={alt}
              onChange={(e) => updateAlt(e.target.value)}
              margin="normal"
              disabled={loading}
              inputProps={{ step: "any" }}
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={surveyInMode}
                  onChange={(e) => setSurveyInMode(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Survey In Mode"
              sx={{ mt: 2 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Survey In Mode: Enable survey-in mode for the RTK station
            </Typography>
          </Box>
        )}

        {/* Register New Site Operation Fields - Only for unregistered stations */}
        {operation === 'register_new_site' && !isRegistered && (
          <Box>
            <TextField
              fullWidth
              label="Site Name"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              margin="normal"
              disabled={loading}
              required
              placeholder="Enter site name (e.g., 'Carteav Office')"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Register New Site: Set the site name for an unregistered station.
              <br />
              The server will automatically assign a random port (between 3001-9999) and register the station.
              <br />
              The list will refresh automatically after successful registration.
            </Typography>
          </Box>
        )}

        {/* Update Site Operation Fields - Only for registered stations */}
        {operation === 'update_site' && isRegistered && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Current Name: <strong>{station?.station_name}</strong>
            </Typography>
            <TextField
              fullWidth
              label="New Site Name"
              value={newSiteName}
              onChange={(e) => setNewSiteName(e.target.value)}
              margin="normal"
              disabled={loading}
              required
              placeholder="Enter new site name"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Update Site Name: Change the name of a registered station.
              <br />
              This only updates the name in the database, no port changes.
            </Typography>
          </Box>
        )}

        {/* Success Message */}
        {success && (
          <Typography 
            color="success.main" 
            sx={{ 
              mt: 2, 
              p: 1.5, 
              backgroundColor: 'success.light', 
              borderRadius: 1,
              color: 'success.dark'
            }}
          >
            ✓ {success}
          </Typography>
        )}

        {/* Error Message */}
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="primary"
          disabled={loading || !operation}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>

      {/* Confirmation Dialog for register_new_site */}
      <Dialog
        open={showConfirmation}
        onClose={handleCancelConfirmation}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 2000 }}
      >
        <DialogTitle className="display_center">
          Confirm New Site Registration
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 'bold',
              mb: 2,
              textAlign: 'center'
            }}
          >
            When confirming, the Server will create a new Socket Connection with a random Port:
          </Typography>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Site Name: {siteName}
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            The list will refresh automatically after successful registration.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button 
            onClick={handleCancelConfirmation}
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Confirm Registration
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

